import { useEffect, useMemo, useRef, useState } from "react";
import { BOOKING_STATUS_OPTIONS } from "../adminConstants";
import {
  mapAdminBookingDetail,
  mapAdminBookingSummary,
} from "../adminUtils";
import {
  getAdminBooking,
  getAdminBookings,
  resendAdminBookingToken,
  revokeAdminBookingToken,
  updateAdminBooking,
  updateAdminBookingStatus,
} from "../../../api/adminBookings";
import { reissueAdminBookingToken } from "../../../api/adminTokens";

const useAdminBookings = ({ t, locations, showFeedback, refreshAudit }) => {
  const [bookings, setBookings] = useState([]);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState("");
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [bookingsFrom, setBookingsFrom] = useState(null);
  const [bookingsTo, setBookingsTo] = useState(null);
  const [sortTimeDir, setSortTimeDir] = useState("desc");
  const [sortPriceDir, setSortPriceDir] = useState("none");
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [bookingDetailsById, setBookingDetailsById] = useState({});
  const [bookingDetailsLoadingId, setBookingDetailsLoadingId] = useState(null);
  const [correctionBookingId, setCorrectionBookingId] = useState(null);
  const [correctionNote, setCorrectionNote] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [bookingTokenActionId, setBookingTokenActionId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [issueUpdatingId, setIssueUpdatingId] = useState(null);

  const announceSearchRef = useRef(false);

  const bookingStatusOptions = useMemo(() => BOOKING_STATUS_OPTIONS, []);
  const bookingStatusChoices = useMemo(
    () => BOOKING_STATUS_OPTIONS.filter((status) => status !== "all"),
    []
  );
  const bookingLocationOptions = useMemo(() => {
    return [
      { value: "all", label: t("admin.bookings.allLocations", "All locations") },
      ...locations.map((location) => ({ value: location.id, label: location.name })),
    ];
  }, [locations, t]);

  const totalBookingsPages = Math.max(1, Math.ceil(bookingsTotal / bookingsPageSize));

  useEffect(() => {
    let cancelled = false;

    const loadBookings = async () => {
      setBookingsLoading(true);
      setBookingsError("");
      try {
        const fromValue = bookingsFrom ? new Date(bookingsFrom) : null;
        const toValue = bookingsTo ? new Date(bookingsTo) : null;
        const payload = await getAdminBookings({
          q: appliedQuery || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
          locationId: locationFilter !== "all" ? locationFilter : undefined,
          sortTime: sortTimeDir !== "none" ? sortTimeDir : undefined,
          sortPrice: sortPriceDir !== "none" ? sortPriceDir : undefined,
          issuesOnly: issuesOnly ? true : undefined,
          from: fromValue && !Number.isNaN(fromValue.getTime()) ? fromValue : undefined,
          to: toValue && !Number.isNaN(toValue.getTime()) ? toValue : undefined,
          page: bookingsPage,
          pageSize: bookingsPageSize,
        });
        if (cancelled) return;
        const items = Array.isArray(payload?.items)
          ? payload.items.map(mapAdminBookingSummary)
          : [];
        setBookings(items);
        setBookingsTotal(payload?.totalCount ?? items.length);
        if (announceSearchRef.current) {
          showFeedback(
            `Found ${items.length} booking${items.length === 1 ? "" : "s"}.`,
            "info"
          );
          announceSearchRef.current = false;
        }
      } catch (error) {
        if (cancelled) return;
        setBookingsError(error?.message || "Failed to load bookings.");
        setBookings([]);
        setBookingsTotal(0);
        if (announceSearchRef.current) {
          showFeedback(error?.message || "Failed to load bookings.", "warning");
          announceSearchRef.current = false;
        }
      } finally {
        if (!cancelled) setBookingsLoading(false);
      }
    };

    loadBookings();

    return () => {
      cancelled = true;
    };
  }, [
    appliedQuery,
    bookingsFrom,
    bookingsPage,
    bookingsPageSize,
    bookingsTo,
    issuesOnly,
    locationFilter,
    sortPriceDir,
    sortTimeDir,
    statusFilter,
    showFeedback,
  ]);

  const handleSearch = () => {
    const query = searchQuery.trim();
    setAppliedQuery(query);
    announceSearchRef.current = true;
    setBookingsPage(1);
  };

  const handleResetFilters = () => {
    setStatusFilter("all");
    setLocationFilter("all");
    setSortTimeDir("desc");
    setSortPriceDir("none");
    setIssuesOnly(false);
    setBookingsFrom(null);
    setBookingsTo(null);
    setBookingsPage(1);
    showFeedback("Filters reset.", "info");
  };

  const applyBookingDetail = (bookingId, detail) => {
    setBookingDetailsById((prev) => ({ ...prev, [bookingId]: detail }));
    setBookings((prev) =>
      prev.map((booking) => (booking.id === bookingId ? { ...booking, ...detail } : booking))
    );
  };

  const handleBookingDetailsToggle = (bookingId) => {
    const willExpand = expandedBookingId !== bookingId;
    setExpandedBookingId((prev) => (prev === bookingId ? null : bookingId));
    setCorrectionBookingId(null);
    if (willExpand && !bookingDetailsById[bookingId]) {
      setBookingDetailsLoadingId(bookingId);
      getAdminBooking(bookingId)
        .then((payload) => {
          const detail = mapAdminBookingDetail(payload);
          applyBookingDetail(bookingId, detail);
        })
        .catch((error) => {
          showFeedback(error?.message || "Failed to load booking details.", "warning");
        })
        .finally(() => {
          setBookingDetailsLoadingId((current) => (current === bookingId ? null : current));
        });
    }
  };

  const handleManualCorrection = (bookingId) => {
    const target = bookings.find((booking) => booking.id === bookingId);
    setExpandedBookingId(bookingId);
    setCorrectionBookingId(bookingId);
    setCorrectionNote(target?.adminNote || "");
    showFeedback(`Admin note opened for ${bookingId}.`, "info");
  };

  const handleCorrectionSubmit = async (event, bookingId) => {
    event.preventDefault();
    const note = correctionNote.trim();
    if (!note) {
      showFeedback("Add an admin note before saving.", "warning");
      return;
    }
    setIsSavingNote(true);
    try {
      const updated = await updateAdminBooking(bookingId, { adminNote: note });
      const mapped = mapAdminBookingSummary(updated);
      setBookings((prev) =>
        prev.map((booking) => (booking.id === bookingId ? mapped : booking))
      );
      await refreshAudit({ silent: true });
      showFeedback("Admin note saved.", "success");
      setCorrectionBookingId(null);
      setCorrectionNote("");
    } catch (error) {
      showFeedback(error?.message || "Failed to save admin note.", "warning");
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleStatusOverride = async (booking, nextStatus) => {
    if (!booking) return;
    const reason = window.prompt("Reason for status change (optional)", "");
    setStatusUpdatingId(booking.id);
    try {
      const updated = await updateAdminBookingStatus(booking.id, {
        status: nextStatus,
        reason: reason || null,
      });
      const mapped = mapAdminBookingSummary(updated);
      setBookings((prev) =>
        prev.map((item) => (item.id === booking.id ? mapped : item))
      );
      await refreshAudit({ silent: true });
      showFeedback(`${booking.id} marked ${nextStatus}.`, "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to update status.", "warning");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleIssueUpdate = async (booking, nextIssue) => {
    if (!booking) return;
    setIssueUpdatingId(booking.id);
    try {
      const payload = await updateAdminBooking(booking.id, {
        issueTag: nextIssue === "none" ? "" : nextIssue,
      });
      const mapped = mapAdminBookingSummary(payload);
      setBookings((prev) =>
        prev.map((item) => (item.id === booking.id ? mapped : item))
      );
      await refreshAudit({ silent: true });
      showFeedback("Issue updated.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to update issue.", "warning");
    } finally {
      setIssueUpdatingId(null);
    }
  };

  const setBookingTokenSummary = (bookingId, bookingToken) => {
    setBookingDetailsById((prev) => ({
      ...prev,
      [bookingId]: { ...(prev[bookingId] || {}), bookingToken },
    }));
    setBookings((prev) =>
      prev.map((booking) =>
        booking.id === bookingId ? { ...booking, bookingToken } : booking
      )
    );
  };

  const handleResendBookingToken = async (bookingId) => {
    setBookingTokenActionId(bookingId);
    try {
      const token = await resendAdminBookingToken(bookingId);
      setBookingTokenSummary(bookingId, {
        actionUrl: token.actionUrl,
        expiresUtc: token.expiresUtc,
        status: "Active",
      });
      await refreshAudit({ silent: true });
      showFeedback("QR code resent.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to resend QR code.", "warning");
    } finally {
      setBookingTokenActionId(null);
    }
  };

  const handleReissueBookingToken = async (bookingId) => {
    if (!window.confirm("Recreate this QR code? The old one will stop working.")) {
      return;
    }
    setBookingTokenActionId(bookingId);
    try {
      const token = await reissueAdminBookingToken(bookingId);
      setBookingTokenSummary(bookingId, {
        actionUrl: token.actionUrl,
        expiresUtc: token.expiresUtc,
        status: "Active",
      });
      await refreshAudit({ silent: true });
      showFeedback("QR code recreated.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to recreate QR code.", "warning");
    } finally {
      setBookingTokenActionId(null);
    }
  };

  const handleRevokeBookingToken = async (bookingId) => {
    if (!window.confirm("Cancel this QR code? It will no longer be valid.")) {
      return;
    }
    setBookingTokenActionId(bookingId);
    try {
      await revokeAdminBookingToken(bookingId, { reason: "revoked_by_owner" });
      setBookingTokenSummary(bookingId, {
        actionUrl: null,
        expiresUtc: null,
        status: "Revoked",
      });
      await refreshAudit({ silent: true });
      showFeedback("QR code cancelled.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to cancel QR code.", "warning");
    } finally {
      setBookingTokenActionId(null);
    }
  };

  return {
    bookings,
    setBookings,
    bookingsTotal,
    bookingsLoading,
    bookingsError,
    bookingsPage,
    setBookingsPage,
    bookingsPageSize,
    searchQuery,
    setSearchQuery,
    appliedQuery,
    setAppliedQuery,
    statusFilter,
    setStatusFilter,
    locationFilter,
    setLocationFilter,
    bookingsFrom,
    setBookingsFrom,
    bookingsTo,
    setBookingsTo,
    sortTimeDir,
    setSortTimeDir,
    sortPriceDir,
    setSortPriceDir,
    issuesOnly,
    setIssuesOnly,
    expandedBookingId,
    setExpandedBookingId,
    bookingDetailsById,
    bookingDetailsLoadingId,
    correctionBookingId,
    setCorrectionBookingId,
    correctionNote,
    setCorrectionNote,
    isSavingNote,
    bookingTokenActionId,
    statusUpdatingId,
    issueUpdatingId,
    bookingStatusOptions,
    bookingStatusChoices,
    bookingLocationOptions,
    totalBookingsPages,
    handleSearch,
    handleResetFilters,
    handleBookingDetailsToggle,
    handleManualCorrection,
    handleCorrectionSubmit,
    handleStatusOverride,
    handleIssueUpdate,
    handleResendBookingToken,
    handleReissueBookingToken,
    handleRevokeBookingToken,
  };
};

export default useAdminBookings;
